
import { useState, useEffect } from "react";
import { useParams, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import EmptyState from "@/components/EmptyState";
import SellerSidebar from "@/components/seller/SellerSidebar";
import SellerAbout from "@/components/seller/SellerAbout";
import SellerOffers from "@/components/seller/SellerOffers";
import SellerReviewsTab from "@/components/seller/SellerReviewsTab";
import SellerProfileLoading from "@/components/seller/SellerProfileLoading";
import { useSellerData } from "@/hooks/useSellerData";
import OpenGraphImage from "@/components/common/OpenGraphImage";
import { generateOgDescription, getOgImagePath } from "@/lib/utils/ogImageUtils";

const SellerProfilePage = () => {
  const { sellerId } = useParams<{ sellerId: string }>();
  const [activeTab, setActiveTab] = useState("offers");

  const {
    seller,
    offers,
    reviews,
    isLoading,
    hasError
  } = useSellerData(sellerId || "");

  if (isLoading) {
    return <SellerProfileLoading />;
  }

  // If loaded by ID and seller has a slug, redirect to the slug URL
  if (seller?.slug && sellerId !== seller.slug && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(sellerId || '')) {
    return <Navigate to={`/sellers/${seller.slug}`} replace />;
  }

  if (hasError || !seller) {
    return (
      <div className="container mx-auto py-12 px-4">
        <EmptyState 
          title="Seller Not Found"
          description="The seller profile you're looking for doesn't exist or has been removed."
          icon="error"
        />
      </div>
    );
  }

  const averageRating = reviews?.length 
    ? (reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length).toFixed(1)
    : "0.0";

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', { 
      year: 'numeric', 
      month: 'long'
    }).format(date);
  };

  return (
    <>
      <OpenGraphImage 
        title={`${seller.name} | BackLink Hub`}
        description={generateOgDescription('seller', seller)}
        image={getOgImagePath('seller', seller)}
        type="profile"
      />
      
      <div className="container mx-auto py-8 px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          <div className="lg:col-span-1">
            <SellerSidebar
              seller={seller}
              averageRating={averageRating}
              reviewCount={reviews?.length || 0}
              joinDate={formatDate(seller.created_at)}
            />
          </div>

          <div className="lg:col-span-3 space-y-6">
            <SellerAbout name={seller.name} bio={seller.bio || ""} />

            <Tabs defaultValue="offers" value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="offers">
                  Offers ({offers?.length || 0})
                </TabsTrigger>
                <TabsTrigger value="reviews">
                  Reviews ({reviews?.length || 0})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="offers" className="mt-6">
                <SellerOffers
                  offers={offers || []}
                  sellerName={seller.name}
                  sellerId={seller.id}
                  averageRating={averageRating}
                  reviewCount={reviews?.length || 0}
                  sellerSlug={seller.slug}
                />
              </TabsContent>

              <TabsContent value="reviews" className="mt-6">
                <Card>
                  <SellerReviewsTab sellerId={seller.id} />
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </>
  );
};

export default SellerProfilePage;
